import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";

import { Alert, Button, Card, TextField } from "../../components/ui";
import { ApiError, api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { User } from "../../lib/types";
import { loginSchema, type LoginValues } from "./schemas";

interface LoginResponse {
  mfa_required: boolean;
  user: User | null;
}

export function LoginPage() {
  const { setUser } = useAuth();
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  const submit = async (values: LoginValues) => {
    setError(null);
    setUnverified(false);
    try {
      const res = await api.post<LoginResponse>("/auth/login", {
        ...values,
        mfa_code: needsMfa ? mfaCode.trim() : null,
      });
      if (res.mfa_required) {
        setNeedsMfa(true);
        return;
      }
      // RedirectIfAuthed sends the now signed-in user to the right page.
      if (res.user) setUser(res.user);
    } catch (err) {
      if (err instanceof ApiError && err.code === "email_not_verified") setUnverified(true);
      setError(errorMessage(err));
    }
  };

  const resend = async () => {
    await api
      .post("/auth/resend-verification", { email: getValues("email") })
      .catch(() => undefined);
    setError("If your email still needs to be confirmed, we sent a new link.");
    setUnverified(false);
  };

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold">Sign in</h1>
      <Card>
        <form noValidate onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
          {error && <Alert tone={unverified ? "warning" : "error"}>{error}</Alert>}
          {unverified && (
            <Button variant="secondary" onClick={resend}>
              Send a new confirmation link
            </Button>
          )}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            error={errors.email?.message}
            {...register("email")}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            error={errors.password?.message}
            {...register("password")}
          />
          {needsMfa && (
            <TextField
              label="Verification code"
              hint="Enter the 6-digit code from your authenticator app, or one of your recovery codes."
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
          )}
          <Button type="submit" busy={isSubmitting}>
            {needsMfa ? "Verify and sign in" : "Sign in"}
          </Button>
        </form>
        <div className="mt-4 flex flex-col gap-2 text-sm">
          <Link className="text-brand-700 underline" to="/forgot-password">
            Forgot your password?
          </Link>
          <span>
            New to the portal?{" "}
            <Link className="text-brand-700 underline" to="/register">
              Create an account
            </Link>
          </span>
        </div>
      </Card>
    </div>
  );
}
